import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'wouter'
import { ArrowLeft, UploadCloud, RotateCcw, RefreshCw } from 'lucide-react'
import { JsonForm } from '@/components/WebsiteContent/JsonForm'
import { PageContentService, WebsiteDefaultsService } from '@/services/pageContent'

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

function parseSlug(location: string): string | null {
  const match = location.match(/^\/website-content\/([^/]+)$/)
  return match ? match[1] : null
}

const WebsiteContentEditor: React.FC = () => {
  const [location, setLocation] = useLocation()
  const slug = parseSlug(location) || ''

  const [label, setLabel] = useState('')
  const [content, setContent] = useState<Record<string, unknown> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishedOnce, setPublishedOnce] = useState(false)
  const [previewNonce, setPreviewNonce] = useState(0)

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipNextAutosave = useRef(false)

  useEffect(() => {
    if (slug) load()
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  const load = async () => {
    setIsLoading(true)
    setLoadError('')
    try {
      const draft = await PageContentService.getDraft(slug)
      if (draft) {
        setContent(draft)
        const statuses = await PageContentService.listStatus()
        const row = statuses.find((s) => s.slug === slug)
        setLabel(row?.label || slug)
        setPublishedOnce(Boolean(row?.publishedAt))
      } else {
        const def = await WebsiteDefaultsService.getDefault(slug)
        setContent(def.content)
        setLabel(def.label)
        setPublishedOnce(false)
      }
      skipNextAutosave.current = true
    } catch (err: any) {
      console.error('Failed to load page content:', err)
      setLoadError(err?.message || 'Failed to load this page’s content.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (next: unknown) => {
    setContent(next as Record<string, unknown>)
  }

  // Autosave the draft ~1.2s after the last edit, then bump the preview
  // iframe so it reflects the save (the iframe's own fetch is no-store, so
  // reloading it is enough — no postMessage bridge needed).
  useEffect(() => {
    if (!content) return
    if (skipNextAutosave.current) {
      skipNextAutosave.current = false
      return
    }
    if (saveTimer.current) clearTimeout(saveTimer.current)
    setSaveState('saving')
    saveTimer.current = setTimeout(async () => {
      try {
        await PageContentService.saveDraft(slug, content, label)
        setSaveState('saved')
        setPreviewNonce((n) => n + 1)
      } catch (err) {
        console.error('Autosave failed:', err)
        setSaveState('error')
      }
    }, 1200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content])

  const handlePublish = async () => {
    if (!window.confirm(`Publish these changes to the live "${label}" page now?`)) return
    setIsPublishing(true)
    try {
      // Make sure the latest edits are saved before publishing them.
      if (content) await PageContentService.saveDraft(slug, content, label)
      await PageContentService.publish(slug)
      setPublishedOnce(true)
      setSaveState('saved')
      window.alert('Published. The live site will pick this up within a few minutes.')
    } catch (err: any) {
      window.alert(err?.message || 'Failed to publish.')
    } finally {
      setIsPublishing(false)
    }
  }

  const handleDiscard = async () => {
    if (!window.confirm('Discard unsaved/unpublished edits and revert to what is currently live?')) return
    try {
      await PageContentService.discardDraft(slug)
      await load()
      setPreviewNonce((n) => n + 1)
    } catch (err: any) {
      window.alert(err?.message || 'Failed to discard draft.')
    }
  }

  const previewUrl = useMemo(() => {
    const base = WebsiteDefaultsService.previewUrl(slug)
    return `${base}${base.includes('?') ? '&' : '?'}_=${previewNonce}`
  }, [slug, previewNonce])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-gray-600">Loading page content...</span>
        </div>
      </div>
    )
  }

  if (loadError || !content) {
    return (
      <div className="space-y-4">
        <button onClick={() => setLocation('/website-content')} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Back to Website Content
        </button>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{loadError}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button onClick={() => setLocation('/website-content')} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-4 w-4" /> Back to Website Content
          </button>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">{label}</h1>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            {saveState === 'saving' && 'Saving draft…'}
            {saveState === 'saved' && 'Draft saved'}
            {saveState === 'error' && <span className="text-red-600">Autosave failed</span>}
          </span>
          {publishedOnce && (
            <button
              onClick={handleDiscard}
              className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Discard draft
            </button>
          )}
          <button
            onClick={handlePublish}
            disabled={isPublishing}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            <UploadCloud className="h-4 w-4" /> {isPublishing ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5 max-h-[80vh] overflow-y-auto">
          <JsonForm value={content} onChange={handleChange} />
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Live preview (draft)</p>
            <button
              onClick={() => setPreviewNonce((n) => n + 1)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white" style={{ height: '78vh' }}>
            <iframe title={`Preview of ${label}`} src={previewUrl} className="h-full w-full" />
          </div>
        </div>
      </div>
    </div>
  )
}

export default WebsiteContentEditor
